import * as Yup from 'yup';
import { format } from 'date-fns';
import ClaimantInformationStep from '../CommonSteps/ClaimantInformationStep';
import DatesAndRatesStep from '../CommonSteps/DatesAndRatesStep';
import EconomicLossesStep from './steps/EconomicLossesStep';
import ReviewStep from '../CommonSteps/ReviewStep';
import ReportStep from './steps/ReportStep.jsx';
import ClaimantInformation from '../../components/Report/sections/ClaimantInformation';
import ExecutiveSummary from '../../components/Report/sections/ExecutiveSummary';
import EconomicDamagesSummary from '../../components/Report/sections/EconomicDamagesSummary';
import ReportAppendices from '../../components/Report/sections/ReportAppendices';
import {
  genderOptions as genders,
  educationLevels,
  employmentStatusOptions as employmentStatuses,
} from '../OptionDefinitions';
import {
  getRandomCountry,
  generateRandomName,
  generateRandomDateOfBirth,
  generateRandomIncidentDate,
  getRandomItem,
  generateRandomProvinceOrState,
  getRandomClaimantSector,
} from '../../utils/randomDataGenerator';

export const getDefaultValues= () => {
    console.log('Getting default values for economic damages');

    if (process.env.NODE_ENV === 'development') {
      const randomCountry =  getRandomCountry();
      const randomProvinceOrState = generateRandomProvinceOrState(randomCountry);
      return {
        fullName: generateRandomName(),
        dateOfBirth: generateRandomDateOfBirth(),
        incidentDate: generateRandomIncidentDate(),
        gender: getRandomItem(genders),
        educationLevel: getRandomItem(educationLevels),
        employmentStatus: getRandomItem(employmentStatuses),
        claimantSector: getRandomClaimantSector(randomCountry),
        country: randomCountry,
        provinceOrState: randomProvinceOrState,
        valuationDate: format(new Date(), 'yyyy-MM-dd'),
        discountRate: 0.035,
        growthRate: 0.0275,
        useCustomValues: false,
      };
    }
    return getInitialValues();
}

export const getInitialValues = () => {
  return {
    fullName: '',
    dateOfBirth: '',
    incidentDate: '',
    gender: '',
    educationLevel: '',
    employmentStatus: '',
    claimantSector: '',
    country: '',
    provinceOrState: '',
    useCustomValues: false,
    valuationDate: format(new Date(), 'yyyy-MM-dd'),
    discountRate: 0.035,
    growthRate: 0.0275,
    useCustomValues: false,
  }
}

export const economicDamagesConfig = {
  id: 'economic-damages',
  name: 'Economic Damages',
  enabled: true,
  defaultValues: () => {
    return getDefaultValues()
  },
  steps: [
    {
      id: 'claimant-information',
      name: 'Claimant Information',
      component: ClaimantInformationStep,
      validationSchema: Yup.object({
        fullName: Yup.string().required('Required'),
        incidentDate: Yup.date()
          .required('Incident date is required')
          .max(new Date(), 'Incident date cannot be in the future'),
        dateOfBirth: Yup.date()
          .required('Date of birth is required')
          .max(new Date(), 'Date of birth cannot be in the future')
          .test(
            'is-not-too-old',
            'Claimant cannot be more than 120 years old',
            (value) => {
              if (!value) return true;
              const age = new Date().getFullYear() - new Date(value).getFullYear();
              return age <= 120;
            }
          ),
        country: Yup.string().required('Country is required'),
        provinceOrState: Yup.string().required('Province or state is required'),
      }),
    },
    {
      id: 'dates-and-rates',
      name: 'Dates and Rates',
      component: DatesAndRatesStep,
      validationSchema: Yup.object({
        valuationDate: Yup.date().required('Valuation date is required'),
      }),
    },
    {
      id: 'economic-losses',
      name: 'Economic Losses',
      component: EconomicLossesStep,
    },
    {
      id: 'review',
      name: 'Review',
      component: ReviewStep,
    },
    {
      id: 'report',
      name: 'Report',
      component: ReportStep,
    },
  ],
  reportSections: [
    { id: 'claimant-info', component: ClaimantInformation },
    { id: 'executive-summary', component: ExecutiveSummary },
    { id: 'economic-damages-summary', component: EconomicDamagesSummary },
    { id: 'appendices', component: ReportAppendices },
  ],
};

export default economicDamagesConfig;